import { useCallback, useEffect, useRef, useState } from "react";
import { useVoteActions } from "../components/backend";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import type { Auth, PlaybackEntry, SkipTally, ToastTone } from "../components/contracts";
import { Footer } from "../components/Footer";
import { HostAbsentNotice } from "../components/HostAbsentNotice";
import { useMedia } from "../components/media";
import { NavBar } from "../components/NavBar";
import { Panel } from "../components/Panel";
import { cx, errorText } from "../components/util";

export interface PlaybackProps {
  auth: Auth;
  round: number;
  /** Host, OR anyone once the host has gone quiet. */
  canControl: boolean;
  /** True only in the widened case, so the extra button gets explained. */
  hostAbsent: boolean;
  segmentLength: number;
  /** `[original, ...dubs]` in the room's randomised order. */
  entries: PlaybackEntry[];
  /**
   * The ROOM's cursor, from `rooms.playbackIndex`. Every device renders this,
   * so everyone is on the same dub. Clamped locally because the running order
   * is assembled client-side and a late device may still be a dub short.
   */
  playbackIndex: number;
  skip: SkipTally | undefined;
  /** Host-only: `rounds.setPlaybackIndex`. Absolute, so a retry can't skip. */
  onSetIndex: (index: number) => Promise<void>;
  /** Host-only: `rounds.advancePhase`, which moves the room to voting. */
  onAdvance: () => Promise<void>;
  onToast: (text: string, tone?: ToastTone) => void;
}

/**
 * The original plays first with sound and can be skipped by majority vote.
 * Every dub after it plays video-muted with the performer's recorded audio
 * scheduled against `video.currentTime` — no muxing, ever. Performers are
 * named. That is the joke.
 *
 * The running order is walked from the SHARED cursor on the room, so all
 * devices show the same dub. The host drives it; everyone else follows. Each
 * device still starts its own playback with its own tap — iOS will not let us
 * start audio remotely, and frame-locking separate phones is out of scope. The
 * guarantee is "same dub", not "same frame".
 */
export function Playback({
  auth,
  round,
  canControl,
  hostAbsent,
  segmentLength,
  entries,
  playbackIndex,
  skip,
  onSetIndex,
  onAdvance,
  onToast,
}: PlaybackProps) {
  const media = useMedia();
  const { skipOriginal } = useVoteActions();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  const index = Math.min(Math.max(0, playbackIndex), Math.max(0, entries.length - 1));
  const entry = entries[index];
  const isOriginal = entry?.playerId === null;
  const isLast = index + 1 >= entries.length;

  /**
   * The room moved to another dub — either because this host advanced it or
   * because a follower's subscription caught up. Either way, stop whatever is
   * playing locally and reset to the un-played state for the new entry.
   */
  useEffect(() => {
    media.stopPlayback();
    videoRef.current?.pause();
    setPlaying(false);
    setEnded(false);
  }, [index, media]);

  const next = useCallback(async () => {
    if (!canControl || advancing) return;
    setAdvancing(true);
    try {
      if (isLast) await onAdvance();
      else await onSetIndex(index + 1);
    } catch (err) {
      onToast(errorText(err, "Couldn't move the room on."), "error");
    } finally {
      setAdvancing(false);
    }
  }, [canControl, advancing, isLast, onAdvance, onSetIndex, index, onToast]);

  /** A passed skip vote ends the original the moment it lands. */
  useEffect(() => {
    if (!isOriginal || !skip?.skipped) return;
    media.stopPlayback();
    videoRef.current?.pause();
    setPlaying(false);
    setEnded(true);
  }, [isOriginal, skip?.skipped, media]);

  useEffect(() => () => media.stopPlayback(), [media]);

  /**
   * Same iOS Safari rule as the Record screen: `video.play()` and the
   * AudioContext resume must both be *initiated* synchronously inside the
   * tap, before the first `await`, or the user-activation flag is gone and
   * Safari rejects them. The muted priming play is what unlocks the element
   * so the unmuted original — and `playDub`'s own play() — are allowed later.
   */
  function play() {
    const video = videoRef.current;
    if (!video || !entry) return;

    video.muted = true;
    const videoUnlocked = video.play();
    const audioUnlocked = media.unlock();
    setPlaying(true);

    void (async () => {
      try {
        await videoUnlocked;
        video.pause();
        await audioUnlocked;

        if (isOriginal) {
          await media.playOriginal(video, {
            start: entry.segmentStart,
            length: segmentLength,
          });
        } else {
          await media.playDub(video, {
            audioUrls: entry.audioUrls,
            start: entry.segmentStart,
            length: segmentLength,
          });
        }
        setEnded(true);
      } catch {
        onToast("Playback stalled. Tap play again.", "error");
      } finally {
        setPlaying(false);
      }
    })();
  }

  if (!entry) {
    return (
      <div className="bd-screen">
        <NavBar subnav={<span>Playback</span>} />
        <main className="shell">
          <Panel title="Playback">
            <p className="t-body">Waiting for the dubs to finish uploading…</p>
          </Panel>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bd-screen">
      <NavBar
        subnav={
          <>
            <span>Round {round}</span>
            <span aria-hidden="true">&middot;</span>
            <span>Playback</span>
            <span aria-hidden="true">&middot;</span>
            <span>
              {index + 1}/{entries.length}
            </span>
          </>
        }
      />

      <main className="shell bd-stack-lg">
        <div className="bd-nowplaying bd-slide-in" key={index}>
          <div className="grow">
            <div className="t-label muted">{isOriginal ? "The original" : "Now playing"}</div>
            <div className="bd-nowplaying-name">{entry.performerName}</div>
          </div>
          {isOriginal ? <Badge tone="carbon">Audio on</Badge> : <Badge tone="ready">Dub</Badge>}
        </div>

        <div className="bd-stage">
          <video
            ref={videoRef}
            className="bd-stage-media"
            src={entry.clipUrl}
            playsInline
            preload="auto"
          />
          {!playing && !ended ? (
            <div className="bd-stage-empty">
              <Button
                variant="submit"
                beacon
                disabled={!entry.clipUrl}
                onClick={play}
              >
                {entry.clipUrl ? "Play" : "Loading…"}
              </Button>
            </div>
          ) : null}
        </div>

        {isOriginal && skip ? (
          <Panel title="Skip the original" animate={false}>
            <div className="spread">
              <div className="cluster">
                <div className="bd-tally" aria-hidden="true">
                  {Array.from({ length: skip.threshold }, (_, i) => (
                    <span key={i} className={cx("bd-tally-pip", i < skip.votes && "is-on")} />
                  ))}
                </div>
                <span className="t-label" role="status" aria-live="polite">
                  {skip.votes}/{skip.threshold} asked to skip
                </span>
              </div>
              <Button
                variant="primary"
                disabled={skip.didVote || skip.skipped}
                onClick={() =>
                  void skipOriginal(auth, round).catch((err: unknown) =>
                    onToast(
                      errorText(err, "Couldn't register your skip."),
                      "error",
                    ),
                  )
                }
              >
                {skip.didVote ? "You voted to skip" : "Skip this"}
              </Button>
            </div>
            <p className="t-micro muted">
              A strict majority skips it instantly. Everyone else keeps watching.
            </p>
          </Panel>
        ) : null}

        {ended ? (
          <div className="plate chamfer bd-pad-lg center bd-pop stack">
            {hostAbsent ? <HostAbsentNotice /> : null}
            {canControl ? (
              <Button
                variant="submit"
                block
                beacon
                disabled={advancing}
                onClick={() => void next()}
              >
                {isLast ? "On to voting" : "Next up"}
              </Button>
            ) : (
              <p className="t-body muted" role="status" aria-live="polite">
                {isLast
                  ? "Waiting for the host to open voting."
                  : "Waiting for the host to play the next one."}
              </p>
            )}
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
}
